import {
  Directive,
  Input,
  OnDestroy,
  OnInit,
  ElementRef,
  AfterViewInit,
  output,
  Output,
  EventEmitter,
} from '@angular/core';
import {AbstractControl, FormControl, FormGroup} from '@angular/forms';
import {DynamicFormElementEl, FormDataKeys} from '../../interfaces/fields-components';
import {CustomFieldData, FormAllData, FormAllInsertData} from '../../interfaces/forms/form-element';
import {FieldService} from '../../services/field/field.service';
import {FieldType} from '../../enums/field-type';
import {Subscription, distinctUntilChanged, filter, map, tap} from 'rxjs';

@Directive({
    selector: '[appGenericField]',
    standalone: false
})
export class GenericFieldDirective implements OnInit, OnDestroy {
  formGroup!: FormGroup;
  field!: FormAllData;
  stepName!: string;

  getSubjectObservable!: Subscription;

  @Input() set data(data: DynamicFormElementEl) {
    this.formGroup = data.formGroup;
    if (data.field) {
      this.field = data.field;
    }
    this.stepName = data.stepName ?? 'unknownStep';
    this.initData(data);
  }

  @Output() popoverShow: EventEmitter<{show: boolean; data: DynamicFormElementEl}> =
    new EventEmitter();

  constructor(
    protected fieldService: FieldService,
    protected element: ElementRef,
  ) {}

  ngOnInit(): void {
    this.setUpdateListener();
  }

  setUpdateListener() {
    const id = this.field?.name ?? this.field?.id;
    const formgroup: FormGroup =
      (this.formGroup?.parent?.get(this.stepName) as FormGroup) ?? this.formGroup;
    // const formControl = formgroup.get(id);

    if (id) {
      this.getSubjectObservable?.unsubscribe();
      this.getSubjectObservable = this.callListener(id, formgroup).subscribe();
    }
  }

  callListener(id: string, formgroup: FormGroup) {
    return this.fieldService.getParams(id, this.stepName).pipe(
      distinctUntilChanged(),
      filter((_) => {
        return !!(
          (this.field.name && formgroup?.get(this.field.name)) ||
          this.field.typeInterface === FieldType.TITLE ||
          this.field.typeInterface === FieldType.NOTIFICATION_BOX ||
          this.field.typeInterface === FieldType.TEXT_BOX ||
          this.field.typeInterface === FieldType.CUSTOM
        );
      }),
      map((res: FormAllData) => {
        return this.updateValues(res);
      }),
      filter((res) => Object.keys(res).length > 0),
      tap((res: FormAllData) => {
        const fc = formgroup.get(this.field?.name ?? '');

        if (fc) {
          res.value = typeof res.value === 'undefined' ? (fc.value ?? null) : res.value;
          const disabled = (res as FormAllInsertData).disabled;
          if (typeof disabled !== 'undefined') {
            if (disabled) {
              fc.disable({onlySelf: true, emitEvent: false});
            } else {
              fc.enable({emitEvent: false});
            }
          }

          fc.patchValue(res.value, {emitEvent: false});
        }
        this.field = {...this.field, ...res} as CustomFieldData;
      }),
    );
  }

  updateValues(res: FormAllData): FormAllInsertData {
    const formgroup: FormGroup =
      (this.formGroup?.parent?.get(this.stepName) as FormGroup) ?? this.formGroup;
    const formControl = formgroup.get(this.field.name ?? this.field.id ?? '');
    if (!formControl) {
      return res as FormAllInsertData;
    }

    if (res.hidden && this.field.name) {
      res.value = null;
      // this.fieldService.setParams(this.field.name, {...this.field, ...res}, this.stepName);
      formgroup.removeControl(this.field.name, {emitEvent: false});
      return {...this.field, ...res} as FormAllInsertData;
    }
    const k = Object.keys(res) as (keyof FormAllData)[];
    const resReduced: FormAllData = k.reduce((obj: any, el) => {
      if (res[el] !== this.field[el]) {
        obj[el] = res[el];
      }
      return obj;
    }, {});

    return resReduced as FormAllInsertData;
  }

  initData(_: DynamicFormElementEl) {}

  onPopoverShow(evt: boolean) {
    if (this.popoverShow.observed) {
      this.popoverShow.emit({show: evt, data: {formGroup: this.formGroup, field: this.field}});
    }
  }

  ngOnDestroy(): void {
    const id = this.field?.name ?? this.field?.id ?? '';
    this.fieldService.setParams(id, this.field, this.stepName);
    this.getSubjectObservable?.unsubscribe();
  }
}
